import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";

/**
 * Görev detayında bağımlılık yönetimi (Faz 2).
 *
 * Görevin İKİ yönü de gösterilir: neye bağlı (öncüller) ve kim ona bağlı (ardıllar).
 * Ardılları da göstermek şart — yalnız öncülleri gösterip "bağ yok" demek,
 * kullanıcının o görevi öteleyince kimi etkileyeceğini gizlerdi.
 */

const TYPE_LABEL: Record<string, string> = {
  FS: "Bitir → Başla",
  SS: "Başla → Başla",
  FF: "Bitir → Bitir",
  SF: "Başla → Bitir",
};

interface Props {
  taskId: string;
  projectId: string;
  canManage: boolean;
  onError: (msg: string) => void;
  onOpenTask: (id: string) => void;
}

export function DependencyPanel({ taskId, projectId, canManage, onError, onOpenTask }: Props) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [pick, setPick] = useState<string | null>(null);
  const [type, setType] = useState("FS");
  const [lag, setLag] = useState("0");

  const depsQ = trpc.project.dependency.list.useQuery({ projectId }, { staleTime: 5_000 });
  const tasksQ = trpc.project.task.list.useQuery({ projectId }, { staleTime: 10_000 });

  const invalidate = () => qc.invalidateQueries();
  const createMut = trpc.project.dependency.create.useMutation({ onSuccess: invalidate, onError: (e) => onError(e.message) });
  const removeMut = trpc.project.dependency.remove.useMutation({ onSuccess: invalidate, onError: (e) => onError(e.message) });

  const all = depsQ.data ?? [];
  const incoming = all.filter((d) => d.successorId === taskId);   // bu görev ARDIL — neye bağlı
  const outgoing = all.filter((d) => d.predecessorId === taskId); // bu görev ÖNCÜL — kim buna bağlı

  // Zaten bağlı olanlar ve görevin kendisi seçenek listesinden çıkarılır —
  // sunucu zaten reddeder ama kullanıcıya kesin reddedilecek seçenek sunmayalım.
  /**
   * Seçilebilir görevler — sunucunun KESİN reddedeceği hiçbir seçenek gösterilmez.
   * Elenenler: görevin kendisi · zaten bağlı olanlar · DOLAYLI ardıllar (döngü olur)
   * · ata ve torunlar (üst görevin ilerlemesi zaten çocuklardan türetilir).
   */
  const options = useMemo(() => {
    const rows = tasksQ.data ?? [];
    const self = rows.find((t) => t.id === taskId);
    const taken = new Set<string>([taskId, ...incoming.map((d) => d.predecessorId), ...outgoing.map((d) => d.successorId)]);

    // Bu görevin TÜM ardılları (geçişli): biri öncül yapılırsa döngü oluşur.
    const succOf = new Map<string, string[]>();
    for (const d of all) {
      const l = succOf.get(d.predecessorId) ?? [];
      l.push(d.successorId);
      succOf.set(d.predecessorId, l);
    }
    const reach = new Set<string>();
    const stack = [taskId];
    while (stack.length) {
      const cur = stack.pop()!;
      for (const nx of succOf.get(cur) ?? []) {
        if (!reach.has(nx)) { reach.add(nx); stack.push(nx); }
      }
    }

    return rows
      .filter((t) => !taken.has(t.id) && !reach.has(t.id))
      // Ata/torun: WBS öneki üzerinden (aynı dalda mı?). `1.2` ile `1.2.3` aynı dal.
      .filter((t) => {
        if (!self) return true;
        const a = `${t.wbs}.`, b = `${self.wbs}.`;
        return !a.startsWith(b) && !b.startsWith(a);
      })
      .map((t) => ({ value: t.id, label: t.title, desc: `${t.wbs} · ${t.code}` }));
  }, [tasksQ.data, taskId, incoming, outgoing, all]);

  function submit() {
    if (!pick) return;
    const n = Number(lag);
    if (!Number.isFinite(n)) { onError("Gecikme tam sayı olmalı."); return; }
    createMut.mutate(
      // Yeni bağ: seçilen görev ÖNCÜL, açık olan görev ARDIL ("bu görev şuna bağlı").
      { predecessorId: pick, successorId: taskId, type: type as "FS" | "SS" | "FF" | "SF", lagDays: Math.trunc(n) },
      { onSuccess: () => { setPick(null); setLag("0"); setType("FS"); setAddOpen(false); } },
    );
  }

  const row = (d: (typeof all)[number], dir: "in" | "out") => {
    const otherId = dir === "in" ? d.predecessorId : d.successorId;
    const otherCode = dir === "in" ? d.predecessorCode : d.successorCode;
    const otherTitle = dir === "in" ? d.predecessorTitle : d.successorTitle;
    return (
      <div key={d.id} className={`dep-row ${d.violated ? "bad" : ""}`}>
        <Icon name={dir === "in" ? "arrowLeft" : "arrowRight"} size={12} />
        <button type="button" className="dep-link" onClick={() => onOpenTask(otherId)} title="Göreve git">
          <span className="mono dep-code">{otherCode}</span>{otherTitle}
        </button>
        <span className="dep-type">{TYPE_LABEL[d.type] ?? d.type}</span>
        {d.lagDays !== 0 ? <span className="dep-lag mono">{d.lagDays > 0 ? "+" : ""}{d.lagDays}g</span> : null}
        {d.violated ? (
          <span className="badge-pill b-danger" title="Bu bağ, girilen tarihlerle tutmuyor">
            <Icon name="alertTriangle" size={10} />çelişki
          </span>
        ) : null}
        {canManage ? (
          <button type="button" className="iact danger dep-del" aria-label="Bağı kaldır"
            onClick={() => removeMut.mutate({ id: d.id })} disabled={removeMut.isPending}>
            <Icon name="x" size={12} />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <section className="td-sec">
      <div className="td-sec-head">
        <span className="td-sec-title">
          Bağımlılıklar{depsQ.isSuccess ? ` (${incoming.length + outgoing.length})` : ""}
        </span>
        {canManage ? (
          <button type="button" className="btn-md secondary" onClick={() => setAddOpen((v) => !v)}>
            <Icon name={addOpen ? "x" : "plus"} size={14} />{addOpen ? "Vazgeç" : "Bağ Ekle"}
          </button>
        ) : null}
      </div>

      {addOpen ? (
        <div className="dep-add">
          <div className="form-field dep-add-pick">
            <span className="lbl">Önce tamamlanacak görev</span>
            <SearchSelect value={pick} options={options} onChange={setPick}
              placeholder="Görev seç…" searchPlaceholder="Görev ara…" emptyText="Bağlanabilir görev yok" icon="list2" />
          </div>
          <div className="form-field dep-add-type">
            <label className="lbl" htmlFor="dep-type">Tür</label>
            <select id="dep-type" className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-field dep-add-lag">
            <label className="lbl" htmlFor="dep-lag">Gecikme (gün)</label>
            <input id="dep-lag" className="form-input" value={lag} onChange={(e) => setLag(e.target.value)} inputMode="numeric" />
          </div>
          <button type="button" className="btn-md primary dep-add-btn" onClick={submit} disabled={!pick || createMut.isPending}>
            {createMut.isPending ? "Ekleniyor…" : "Ekle"}
          </button>
        </div>
      ) : null}

      {/* "Bağımlılık yok." KESİN bir iddia — yalnız veri gerçekten geldiyse yazılır.
          Eskiden yükleme ve HATA durumunda da yazılıyordu; kullanıcı 3 öncülü olan
          bir görevi "bağsız" sanıp öne çekebilirdi. */}
      {depsQ.isLoading ? (
        <p className="td-empty">Yükleniyor…</p>
      ) : depsQ.isError ? (
        <div className="form-banner-err">Bağımlılıklar yüklenemedi: {depsQ.error.message}</div>
      ) : incoming.length === 0 && outgoing.length === 0 ? (
        <p className="td-empty">Bağımlılık yok.</p>
      ) : (
        <div className="dep-list">
          {incoming.length > 0 ? (
            <>
              <span className="dep-group">Bu görev şunlara bağlı</span>
              {incoming.map((d) => row(d, "in"))}
            </>
          ) : null}
          {outgoing.length > 0 ? (
            <>
              <span className="dep-group">Bu göreve bağlı olanlar</span>
              {outgoing.map((d) => row(d, "out"))}
            </>
          ) : null}
        </div>
      )}
    </section>
  );
}
